import { debugLog } from "./utils/debugLog.js";
import { React, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Select from "react-select";
import ReactMarkdown from "react-markdown";
import { ArrowTopRightOnSquareIcon, GlobeAltIcon, InformationCircleIcon, PencilSquareIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { buildCompanyDeepResearchPrompt } from "./utils/companyDeepResearchPrompt.js";
import { normalizeDomain, normalizeUrl } from "./utils/companyJobPostings.js";
import { useUser } from "@clerk/clerk-react";
import { adjustHeadcount, currencyFormat, extractDomain, scrollToCompanyProfileSection, parseDelimitedList } from "./helpers.js";
import {
  countryOptions,
  industryOptions,
  getProvinceOptionsForCountries,
} from "./helperData.js";
import CompanyTeamSection from "./components/CompanyTeamSection.jsx";
import CompanyGrantQuestionsSection from "./components/CompanyGrantQuestionsSection.jsx";
import CompanyCallRecordingsSection from "./components/CompanyCallRecordingsSection.jsx";
import {
  WORKING_CAPITAL_INTEREST_OPTIONS,
  parseWorkingCapitalInterests,
  loadOnboardingGrantInterestsFromCompanyFields,
  ONBOARDING_PROGRESS_FIELD,
} from "./utils/interestedOpportunities.js";
import { parseOnboardingProgress } from "./utils/onboardingSaveUtils.js";
import {
  COMPANY_PROFILE_ONBOARDING_FOCUS_EVENT,
  consumePendingCompanyProfileOnboardingFocus,
  focusCompanyProfileField,
} from "./utils/companyProfileSearch.js";
import { moneyAmountOptionsForValue } from "./utils/moneyAmountOptions.js";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_YEAR_DEFAULT_FOUNDED = CURRENT_YEAR - 2;

const REACT_SELECT_PORTAL_TARGET =
  typeof document !== "undefined" ? document.body : null;

// Source of truth: src/OnboardingV2Page.jsx LegalStructureQuestions. "Profit" is kept
// as a tail option so legacy company records that already store that value still render.
const COMPANY_TYPE_OPTIONS = [
  "Sole Proprietorship",
  "Partnership",
  "Corporation",
  "Limited Liability Partnership (LLP)",
  "Cooperative",
  "Non-profit",
  "Trust",
  "Profit",
].map((v) => ({ value: v, label: v }));

const MONTH_OPTIONS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
].map((v) => ({ value: v, label: v }));

const FOUNDED_YEAR_OPTIONS = (() => {
  const opts = [];
  for (let y = CURRENT_YEAR; y >= 1900; y -= 1) {
    opts.push({ value: y, label: String(y) });
  }
  return opts;
})();

const AUTO_SAVE_DEBOUNCE_MS = 800;
// Parent grid is sm:grid-cols-6
const SHORT_FIELD_COL = "sm:col-span-2"; // 3 columns on desktop
const HALF_FIELD_COL = "sm:col-span-3"; // 2 columns on desktop

const LINKEDIN_URL_HELP = (
  <>
    Team indexing uses this as{" "}
    <span className="font-medium text-gray-700">…/people/</span>
    {" "}(or Org Chart under Key assets if set).
  </>
);

function FieldHelpTip({ children }) {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label="About this field"
        onClick={(e) => e.preventDefault()}
        className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-700"
      >
        <InformationCircleIcon className="h-4 w-4" />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-20 mt-1.5 w-72 rounded-lg border border-gray-100 bg-white px-3 py-2 text-left text-sm font-normal leading-relaxed text-gray-600 opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {children}
      </span>
    </span>
  );
}

const buildOrgChartDefault = (linkedInUrl) => {
  if (!linkedInUrl) return "";
  const base = String(linkedInUrl).trim().replace(/\/+$/, "");
  return `${base}/people/`;
};

const SAAS_COMPANY_WORKBOOK_URL =
  "https://docs.google.com/spreadsheets/d/1A8WImT0A6qtIev7UMEuKr1Mv4fcMKAHl/edit?gid=904223253#gid=904223253";

const isEmptyFormValue = (value) => {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

// Key asset URL fields — clearing the input should PATCH null to Airtable.
const CLEARABLE_FORM_KEYS = new Set([
  "HiringPlan",
  "ProformaFinancialModel",
  "GoogleDoc",
  "GoogleDrive",
  // Empty interest lists must still PATCH Airtable.
  "interestedOpportunities",
  "governmentProgramsInterest",
]);

const SECTION_SCROLL_OFFSET = 120;

export const BASE_SECTION_NAV_ITEMS = [
  { id: "company-interests", label: "Interests" },
  { id: "company-onboarding", label: "Company Earnings Estimate" },
  { id: "company-general", label: "General" },
  { id: "company-details", label: "Strategic Priorities & Expenses" },
  { id: "company-metrics", label: "Metrics" },
  { id: "company-grant-qa", label: "Grant Q&A" },
];

export const TEAM_SECTION_NAV_ITEM = { id: "company-team", label: "Team" };
export const JOB_POSTINGS_NAV_ITEM = {
  id: "company-job-postings",
  label: "Open Job Postings",
};
export const CALL_RECORDINGS_NAV_ITEM = {
  id: "company-call-recordings",
  label: "Call Recordings",
};

export function getCompanySectionNavItems(isAdmin = false) {
  if (!isAdmin) return BASE_SECTION_NAV_ITEMS;

  // Admin-only: Team and Open Job Postings after Strategic Priorities; Call Recordings after Grant Q&A.
  const items = [];
  for (const item of BASE_SECTION_NAV_ITEMS) {
    items.push(item);
    if (item.id === "company-details") {
      items.push(TEAM_SECTION_NAV_ITEM, JOB_POSTINGS_NAV_ITEM);
    }
    if (item.id === "company-grant-qa") {
      items.push(CALL_RECORDINGS_NAV_ITEM);
    }
  }
  return items;
}

const EARNINGS_IMPROVEMENT_OPTIONS = WORKING_CAPITAL_INTEREST_OPTIONS;

const GOVERNMENT_PROGRAMS_INTEREST_OPTIONS = [
  "Hiring - Talent - Training",
  "Market Expansion",
  "Technology Development",
  "Social Impact",
  "Niche/Industry-Specific",
  "Other",
];

const interestOptionId = (prefix, option) =>
  `${prefix}-${option.replace(/\s/g, "-").replace(/[()]/g, "")}`;

function InterestCheckboxGroup({ id, label, description, namePrefix, options, selected, onChange }) {
  return (
    <div id={id}>
      <p className="text-sm font-semibold text-gray-900">{label}</p>
      {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((option) => {
          const checked = selected?.includes(option) || false;
          const id = interestOptionId(namePrefix, option);

          return (
            <label
              key={option}
              htmlFor={id}
              className={[
                "inline-flex cursor-pointer items-center rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150",
                checked
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
              ].join(" ")}
            >
              <input
                id={id}
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={(e) => {
                  const current = selected || [];
                  onChange(
                    e.target.checked
                      ? [...current, option]
                      : current.filter((item) => item !== option)
                  );
                }}
              />
              {option}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function SectionHeading({ id, title, description, action, className = "" }) {
  return (
    <div id={id} className={`col-span-full scroll-mt-24 ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
    </div>
  );
}

const FIELD_CONTROL_CLASS =
  "block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors";

function OverlappingField({ id, label, help, className = "", children }) {
  return (
    <div className={`relative ${className}`}>
      <label
        htmlFor={id}
        className="absolute -top-2 left-2 z-10 inline-flex max-w-[calc(100%-0.75rem)] items-center gap-1 bg-white px-1 text-xs font-medium text-gray-900"
      >
        <span className="truncate">{label}</span>
        {help}
      </label>
      {children}
    </div>
  );
}

const sectionNavLinkClass = (isActive) =>
  [
    "flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
    isActive
      ? "bg-gray-900 text-white"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
  ].join(" ");

function OpenAILogo({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.758a.775.775 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.163a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"
      />
    </svg>
  );
}

function AnthropicLogo({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#D97757"
        d="M12 2 2.5 19.5h4.1L12 11.2l5.4 8.3H22L12 2zm0 8.2 2.8 4.3H9.2L12 10.2z"
      />
    </svg>
  );
}

function ModelProviderLogo({ provider }) {
  if (provider === "anthropic") {
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#D97757]/10">
        <AnthropicLogo className="h-3.5 w-3.5" />
      </span>
    );
  }

  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-gray-900">
      <OpenAILogo className="h-3 w-3 text-white" />
    </span>
  );
}

const DEEP_RESEARCH_MODEL_OPTIONS = [
  { value: "gpt-5.2", label: "GPT-5.2 (Recommended)", provider: "openai" },
  { value: "gpt-5", label: "GPT-5", provider: "openai" },
  { value: "gpt-5-mini", label: "GPT-5 Mini", provider: "openai" },
  { value: "gpt-5-nano", label: "GPT-5 Nano", provider: "openai" },
  { value: "claude-sonnet-4", label: "Claude Sonnet 4", provider: "anthropic" },
  { value: "claude-opus-4", label: "Claude Opus 4", provider: "anthropic" },
];

const DEEP_RESEARCH_MARKDOWN_COMPONENTS = {
  h1: ({ children }) => (
    <h1 className="mb-3 mt-4 text-lg font-semibold tracking-tight text-gray-900 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-6 text-base font-semibold text-gray-900 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-4 text-sm font-semibold text-gray-900">{children}</h3>
  ),
  p: ({ children }) => <p className="mb-3 text-sm leading-6 text-gray-600 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="mb-3 list-disc space-y-1 pl-5 text-sm text-gray-600">{children}</ul>,
  ol: ({ children }) => <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm text-gray-600">{children}</ol>,
  li: ({ children }) => <li className="leading-6">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} className="font-medium text-gray-900 underline underline-offset-2 hover:text-gray-700" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const formatDeepResearchModelOption = (option) => (
  <div className="flex items-center gap-2.5">
    <ModelProviderLogo provider={option.provider} />
    <span className="text-sm text-gray-900">{option.label}</span>
  </div>
);

function formatExpenseAmount(value) {
  if (value == null || value === "") return null;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return String(value);
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return String(value);
  return currencyFormat(n);
}

function hasOnboardingValue(value) {
  return value !== undefined && value !== null && value !== "";
}

function toOnboardingList(value) {
  if (Array.isArray(value)) return value.filter(hasOnboardingValue);
  if (!hasOnboardingValue(value)) return [];
  return String(value)
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function humanizeOnboardingLabel(value) {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatOnboardingDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function aggregateOnboardingExpenses(items) {
  const aggregated = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const name = String(item?.name || item?.toolName || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    const amount =
      Number(String(item?.amount ?? item?.expense ?? "").replace(/[^0-9.-]/g, "")) || 0;
    const existing = aggregated.get(key) || { name, amount: 0, count: 0 };
    existing.amount += amount;
    existing.count += 1;
    aggregated.set(key, existing);
  });
  return [...aggregated.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function extractPrefixedOnboardingExpenses(data, prefix) {
  return Object.entries(data || {})
    .filter(([key, value]) => {
      return (
        key.startsWith(`${prefix}_`) &&
        key.endsWith("_expense") &&
        hasOnboardingValue(value)
      );
    })
    .map(([key, amount]) => ({
      label: key.slice(prefix.length + 1, -"_expense".length),
      amount,
    }));
}

function OnboardingChips({ items, tone = "neutral" }) {
  const values = toOnboardingList(items);
  if (values.length === 0) return null;
  const toneClass =
    tone === "success"
      ? "bg-emerald-50 text-emerald-700"
      : "bg-gray-100 text-gray-700";

  return (
    <div className="flex flex-wrap gap-2">
      {values.map((item, index) => (
        <span
          key={`${String(item)}-${index}`}
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium ${toneClass}`}
        >
          {String(item)}
        </span>
      ))}
    </div>
  );
}

function OnboardingFacts({ items }) {
  const visibleItems = items.filter((item) => hasOnboardingValue(item.value));
  if (visibleItems.length === 0) return null;

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
      {visibleItems.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-sm text-gray-500">{item.label}</dt>
          <dd className="mt-1 break-words text-sm font-medium text-gray-900">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function OnboardingExpenseRows({ items }) {
  const visibleItems = items.filter(
    (item) => item?.label && hasOnboardingValue(item?.amount)
  );
  if (visibleItems.length === 0) return null;

  return (
    <ul className="divide-y divide-gray-100">
      {visibleItems.map((item) => (
        <li key={item.label} className="flex items-center justify-between gap-4 py-2.5">
          <span className="text-sm text-gray-600">
            {item.label}
            {item.count > 1 ? ` (${item.count} entries)` : ""}
          </span>
          <span className="shrink-0 text-sm font-medium text-gray-900">
            {formatExpenseAmount(item.amount)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function OnboardingSection({ id, title, description, children, isActive = true, stacked = false }) {
  if (!isActive) return null;

  return (
    <section
      id={id}
      className={
        stacked
          ? "scroll-mt-24 rounded-xl border border-gray-100 bg-white p-6"
          : "scroll-mt-24 rounded-b-xl border border-t-0 border-gray-100 bg-white p-6"
      }
    >
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

/** Read-only, structured summary of companies.Onboarding. */
export function OnboardingSummary({
  companyFields,
  layout = "tabs",
  showHeader = true,
  showRaw = true,
}) {
  const [activeTab, setActiveTab] = useState("company");

  useEffect(() => {
    const apply = (detail) => {
      if (!detail?.tab) return;
      setActiveTab(detail.tab);
      if (detail.focusId) {
        window.setTimeout(() => focusCompanyProfileField(detail.focusId), 80);
        window.setTimeout(() => focusCompanyProfileField(detail.focusId), 360);
      }
    };

    const pending = consumePendingCompanyProfileOnboardingFocus();
    if (pending) apply(pending);

    const onFocus = (event) => {
      consumePendingCompanyProfileOnboardingFocus();
      apply(event.detail);
    };
    window.addEventListener(COMPANY_PROFILE_ONBOARDING_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(COMPANY_PROFILE_ONBOARDING_FOCUS_EVENT, onFocus);
  }, []);
  const rawOnboarding = companyFields?.[ONBOARDING_PROGRESS_FIELD];
  const progress = useMemo(
    () => parseOnboardingProgress(rawOnboarding),
    [rawOnboarding]
  );
  const grantPayload = useMemo(
    () => loadOnboardingGrantInterestsFromCompanyFields(companyFields || {}),
    [companyFields]
  );
  const prettyOnboarding = useMemo(() => {
    if (!rawOnboarding || String(rawOnboarding).trim() === "") return null;
    try {
      return JSON.stringify(JSON.parse(String(rawOnboarding)), null, 2);
    } catch {
      return String(rawOnboarding);
    }
  }, [rawOnboarding]);

  if (!progress) {
    return (
      <div className="col-span-full">
        <h3 className="text-lg font-semibold leading-7 text-gray-900">Company Earnings Estimate</h3>
        <p className="mt-2 text-sm text-gray-500">
          No company earnings estimate has been saved yet.
        </p>
        <Link
          to="/company-earnings-estimate"
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800"
        >
          Start company earnings estimate
        </Link>
      </div>
    );
  }

  const data = progress.formData || {};
  const softwareExpenses = aggregateOnboardingExpenses(data.softwareExpenses);
  const aiExpenses = aggregateOnboardingExpenses(data.aiExpenses);
  const otherExpenses = aggregateOnboardingExpenses(data.otherExpenses);
  const trainingExpenses = aggregateOnboardingExpenses(data.trainingCourses);
  const trainingCourseNames = (Array.isArray(data.trainingCourses)
    ? data.trainingCourses
    : []
  )
    .map((course) =>
      typeof course === "object"
        ? course?.name || course?.courseName
        : String(course || "").trim()
    )
    .filter(Boolean);
  const contractorExpenses = extractPrefixedOnboardingExpenses(
    data,
    "hiringContractor"
  );
  const automationExpenses = extractPrefixedOnboardingExpenses(data, "automation");
  const grantExpenses = Object.entries(grantPayload.expenses || {}).map(
    ([label, amount]) => ({ label, amount })
  );
  const clientOfferExpenses = Object.entries(data.clientOfferExpenses || {}).map(
    ([label, amount]) => ({ label, amount })
  );
  const teamEmails = [data.teamEmail1, data.teamEmail2, data.teamEmail3].filter(Boolean);
  const agreedGrants = toOnboardingList(data.agreedToGrants);
  const hiringRoles = (Array.isArray(data.hiringRoles) ? data.hiringRoles : [])
    .filter((role) => role?.role)
    .map((role) => `${role.role}${role.quarter ? ` · ${role.quarter}` : ""}`);
  const updatedAt = formatOnboardingDate(progress.updatedAt);
  const stacked = layout === "stacked";
  const sectionActive = (id) => stacked || activeTab === id;
  const onboardingTabs = [
    { id: "company", label: "Company" },
    { id: "software", label: "Software" },
    { id: "grants", label: "Grants" },
    { id: "client-offer", label: "Client offers" },
    { id: "hiring", label: "Hiring" },
    { id: "growth", label: "Growth" },
    { id: "ai", label: "AI & automation" },
    { id: "expenses", label: "Expenses" },
    { id: "team", label: "Team" },
  ];

  return (
    <div className="col-span-full space-y-4">
      {showHeader && (
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <h3 className="text-lg font-semibold leading-7 text-gray-900">Company Earnings Estimate</h3>
            <p className="mt-1 text-sm text-gray-500">
              A read-only summary of the saved company earnings estimate.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium ${
                progress.completed
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}
            >
              {progress.completed ? "Complete" : "In progress"}
            </span>
            <span className="text-sm text-gray-400">
              {progress.completedSubsections.length} steps saved
            </span>
            {updatedAt && <span className="text-sm text-gray-400">Updated {updatedAt}</span>}
          </div>
        </div>
      )}

      <div className={stacked ? "space-y-4" : undefined}>
        {!stacked && (
        <div
          role="tablist"
          aria-label="Company earnings estimate details"
          className="flex w-full overflow-x-auto rounded-t-xl border border-gray-100 bg-gray-50"
        >
          {onboardingTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={`min-w-max flex-1 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-gray-900 bg-white text-gray-900"
                    : "border-transparent text-gray-500 hover:bg-white hover:text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        )}

        <OnboardingSection id="company-onboarding-company" title="Company" stacked={stacked} isActive={sectionActive("company")}>
          <OnboardingFacts
            items={[
              { label: "Role", value: data.jobTitle },
              { label: "Contact email", value: data.prospectEmail },
              { label: "Employees", value: data.employees },
              { label: "Founded", value: data.founded },
              { label: "Legal structure", value: data.legalStructure },
              {
                label: "Location",
                value: [
                  ...toOnboardingList(data.city),
                  ...toOnboardingList(data.state),
                  ...toOnboardingList(data.country),
                ].join(", "),
              },
              { label: "Annual revenue", value: formatExpenseAmount(data.revenue) },
              { label: "Annual expenses", value: formatExpenseAmount(data.expenses) },
            ]}
          />
          {toOnboardingList(data.industry).length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">Industries</p>
              <OnboardingChips items={data.industry} />
            </div>
          )}
        </OnboardingSection>

        <OnboardingSection
          id="company-onboarding-software"
          title="Software"
          description={`${softwareExpenses.length} unique tools saved`}
          stacked={stacked} isActive={sectionActive("software")}
        >
          <OnboardingFacts
            items={[
              {
                label: "Monthly software spend",
                value: formatExpenseAmount(data.monthlySpend),
              },
              {
                label: "Listed software total",
                value: formatExpenseAmount(
                  softwareExpenses.reduce((sum, item) => sum + item.amount, 0)
                ),
              },
            ]}
          />
          <OnboardingExpenseRows
            items={softwareExpenses.map((item) => ({
              label: item.name,
              amount: item.amount,
              count: item.count,
            }))}
          />
        </OnboardingSection>

        <OnboardingSection
          id="company-onboarding-grants"
          title="Grants and tax credits"
          description="Subsidization priorities and estimated eligible expenses"
          stacked={stacked} isActive={sectionActive("grants")}
        >
          <OnboardingChips items={grantPayload.interests} />
          <OnboardingExpenseRows items={grantExpenses} />
          <OnboardingFacts
            items={[
              { label: "R&D last year", value: formatExpenseAmount(data.rdLastYear) },
              { label: "R&D this year", value: formatExpenseAmount(data.rdThisYear) },
              { label: "Other interest", value: grantPayload.otherLabel },
              { label: "Notes", value: data.governmentGrantsPursuedNotes },
            ]}
          />
          {grantPayload.otherTags?.length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">Other qualifiers</p>
              <OnboardingChips items={grantPayload.otherTags} tone="success" />
            </div>
          )}
          {agreedGrants.length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">Programs agreed to pursue</p>
              <OnboardingChips items={agreedGrants} tone="success" />
            </div>
          )}
          {toOnboardingList(data.excludedGrantPrograms).length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">Excluded programs</p>
              <OnboardingChips
                items={toOnboardingList(data.excludedGrantPrograms).map(
                  humanizeOnboardingLabel
                )}
              />
            </div>
          )}
        </OnboardingSection>

        <OnboardingSection
          id="company-onboarding-client-offer"
          title="Client offer subsidization"
          description="Services the company may want to subsidize for clients"
          stacked={stacked} isActive={sectionActive("client-offer")}
        >
          <OnboardingChips items={data.clientOfferCategories} />
          <OnboardingExpenseRows items={clientOfferExpenses} />
          {hasOnboardingValue(data.clientOfferOtherLabel) && (
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">Other: </span>
              {data.clientOfferOtherLabel}
            </p>
          )}
        </OnboardingSection>

        <OnboardingSection id="company-onboarding-hiring" title="Hiring and talent" stacked={stacked} isActive={sectionActive("hiring")}>
          <OnboardingFacts
            items={[
              { label: "Planned hires", value: data.plannedHires },
              { label: "Current students", value: data.currentStudents },
              { label: "Potential student hires", value: data.potentialStudentHires },
            ]}
          />
          {hiringRoles.length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">Roles and timing</p>
              <OnboardingChips items={hiringRoles} />
            </div>
          )}
          {toOnboardingList(data.hiringContractors).length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">Contractor support</p>
              <OnboardingChips items={data.hiringContractors} />
            </div>
          )}
          <OnboardingExpenseRows items={contractorExpenses} />
        </OnboardingSection>

        <OnboardingSection id="company-onboarding-growth" title="Growth and expansion" stacked={stacked} isActive={sectionActive("growth")}>
          <OnboardingFacts
            items={[
              {
                label: "Business outside Canada",
                value: hasOnboardingValue(data.businessOutsideCanada)
                  ? data.businessOutsideCanada
                    ? "Yes"
                    : "No"
                  : null,
              },
              {
                label: "Outside-Canada investment",
                value: formatExpenseAmount(data.businessOutsideCanada_expense),
              },
              {
                label: "Launching a new product or service",
                value: hasOnboardingValue(data.newProductService)
                  ? data.newProductService
                    ? "Yes"
                    : "No"
                  : null,
              },
              {
                label: "New product or service investment",
                value: formatExpenseAmount(data.newProductService_expense),
              },
            ]}
          />
        </OnboardingSection>

        <OnboardingSection id="company-onboarding-ai" title="AI and automation" stacked={stacked} isActive={sectionActive("ai")}>
          <OnboardingFacts
            items={[
              { label: "Monthly AI spend", value: formatExpenseAmount(data.aiSpend) },
            ]}
          />
          {toOnboardingList(data.aiGoals).length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">AI goals</p>
              <OnboardingChips items={data.aiGoals} />
            </div>
          )}
          {toOnboardingList(data.automationAreas).length > 0 && (
            <div>
              <p className="mb-2 text-sm text-gray-500">Automation opportunities</p>
              <OnboardingChips items={data.automationAreas} tone="success" />
            </div>
          )}
          <OnboardingExpenseRows
            items={aiExpenses.map((item) => ({
              label: item.name,
              amount: item.amount,
              count: item.count,
            }))}
          />
          <OnboardingExpenseRows items={automationExpenses} />
        </OnboardingSection>

        <OnboardingSection
          id="company-onboarding-expenses"
          title="Training and other expenses"
          stacked={stacked} isActive={sectionActive("expenses")}
        >
          {trainingCourseNames.length === 0 &&
          otherExpenses.length === 0 ? (
            <p className="text-sm text-gray-400">No training or other expenses entered.</p>
          ) : (
            <>
              <OnboardingChips items={trainingCourseNames} />
              <OnboardingExpenseRows
                items={trainingExpenses.map((item) => ({
                  label: item.name,
                  amount: item.amount,
                  count: item.count,
                }))}
              />
              <OnboardingExpenseRows
                items={otherExpenses.map((item) => ({
                  label: item.name,
                  amount: item.amount,
                  count: item.count,
                }))}
              />
            </>
          )}
        </OnboardingSection>

        <OnboardingSection id="company-onboarding-team" title="Team" stacked={stacked} isActive={sectionActive("team")}>
          {teamEmails.length > 0 ? (
            <OnboardingChips items={teamEmails} />
          ) : (
            <p className="text-sm text-gray-400">No teammates entered.</p>
          )}
        </OnboardingSection>
      </div>

      {showRaw && prettyOnboarding && (
        <details className="group rounded-xl border border-gray-100 bg-white p-5">
          <summary className="cursor-pointer list-none text-sm font-medium text-gray-600 transition-colors hover:text-gray-900">
            View raw earnings estimate data
          </summary>
          <pre className="mt-4 max-h-96 overflow-auto rounded-lg bg-gray-50 p-4 text-sm leading-6 text-gray-700">
            {prettyOnboarding}
          </pre>
        </details>
      )}
    </div>
  );
}

function CompanyProfileFields({
  company,
  isLoading,
  isEditable = false,
  onSave = null,
  /** When true, hide the left section nav (parent page owns navigation). */
  hideSectionNav = false,
  /** Optional external active section id (for parent scroll-spy / deep links). */
  activeSectionId = null,
  /** Notify parent when the active section changes via scroll or in-form nav. */
  onActiveSectionChange = null,
  /** Notify parent of save button state (for sticky sidebar controls). */
  onSaveControlsChange = null,
}) {
  const { user, isLoaded: isUserLoaded } = useUser();
  const isAdmin = useMemo(() => {
    if (!isUserLoaded || !user?.primaryEmailAddress?.emailAddress) return false;
    return extractDomain(user.primaryEmailAddress.emailAddress) === process.env.REACT_APP_COOL_EMAIL;
  }, [isUserLoaded, user]);

  const sectionNavItems = useMemo(
    () => getCompanySectionNavItems(isAdmin),
    [isAdmin]
  );

  const sectionNavIds = useMemo(
    () => sectionNavItems.map((item) => item.id),
    [sectionNavItems]
  );

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState(null);
  const [formReady, setFormReady] = useState(false);
  const [isResearching, setIsResearching] = useState(false);
  const [researchError, setResearchError] = useState(null);
  const [researchSuccess, setResearchSuccess] = useState(false);
  const [researchModel, setResearchModel] = useState("gpt-5.2");
  const [researchPrompt, setResearchPrompt] = useState("");
  const [isEditingResearchReport, setIsEditingResearchReport] = useState(false);
  const [isScrapingWebsite, setIsScrapingWebsite] = useState(false);
  const [websiteScrapeError, setWebsiteScrapeError] = useState(null);
  const [websiteScrapeSuccess, setWebsiteScrapeSuccess] = useState(false);
  const [activeSection, setActiveSectionState] = useState("company-interests");
  const activeSectionRef = useRef(activeSection);
  activeSectionRef.current = activeSection;
  const onActiveSectionChangeRef = useRef(onActiveSectionChange);
  onActiveSectionChangeRef.current = onActiveSectionChange;

  // Resolve the next section here instead of inside the state updater: React runs
  // updaters during render, so notifying the parent from there updates another
  // component mid-render (and can fire twice under StrictMode).
  const setActiveSection = useCallback((next) => {
    const value =
      typeof next === "function" ? next(activeSectionRef.current) : next;
    if (value === undefined) return;
    activeSectionRef.current = value;
    setActiveSectionState(value);
    if (value && onActiveSectionChangeRef.current) {
      onActiveSectionChangeRef.current(value);
    }
  }, []);

  const resolvedActiveSection = activeSectionId || activeSection;
  const initialized = useRef(false);
  const prevCompanyIdRef = useRef(null);
  const formDataRef = useRef({});
  const onSaveRef = useRef(onSave);
  const lastSavedSnapshotRef = useRef(null);
  const skipAutoSaveRef = useRef(false);
  const autoSaveTimeoutRef = useRef(null);
  const savedStatusTimeoutRef = useRef(null);
  const isScrollingToSectionRef = useRef(false);

  formDataRef.current = formData;
  onSaveRef.current = onSave;

  // Working-capital interests (positive). Onboarding grant JSON lives in OnboardingGrantInterests.
  const parseInterestedOpportunitiesField = (value) => parseWorkingCapitalInterests(value);

  const parseGovernmentProgramsField = (value) =>
    parseDelimitedList(value, GOVERNMENT_PROGRAMS_INTEREST_OPTIONS);

  const parseIndustryField = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
    const optionValues = industryOptions.map((o) => o.value);
    return parseDelimitedList(raw, optionValues);
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  // Apply sensible defaults for top-of-form fields when enrich data is missing.
  const buildFormDataFromCompany = (fields = {}) => {
    const employees = fields.Employees ?? 10;
    return {
      Name: fields.Name ?? "",
      Domain: fields.Domain ?? "",
      Employees: fields.Employees ?? employees,
      Industry: fields.Industry ?? "Technology",
      Description: fields.Description ?? "",
      Address: fields.Address ?? "",
      Country: fields.Country ?? "Canada",
      ProvinceOrState: fields.ProvinceOrState ?? "British Columbia",
      AnnualRevenue: fields.AnnualRevenue ?? employees * 100000,
      CompanyType: fields.CompanyType ?? "Corporation",
      IndigenousTeamMember: fields.IndigenousTeamMember === true,
      WomenLed: fields.WomenLed === true,
      LegalName: fields.LegalName ?? fields.Name ?? "",
      BusinessNumber: fields.BusinessNumber ?? "",
      CCPCStatus: fields.CCPCStatus ?? "",
      TaxableCapitalPriorYear: fields.TaxableCapitalPriorYear ?? "",
      Founded: fields.Founded ?? CURRENT_YEAR_DEFAULT_FOUNDED,
      FiscalYearEnd: fields.FiscalYearEnd ?? "December",
      LinkedInUrl: fields.LinkedInUrl ?? "",
      FollowerCount: fields.FollowerCount ?? "",
      OrgChart: fields.OrgChart ?? buildOrgChartDefault(fields.LinkedInUrl) ?? "",
      HiringPlan: fields.HiringPlan ?? "",
      ProformaFinancialModel: fields.ProformaFinancialModel ?? "",
      GoogleDoc: fields.GoogleDoc ?? "",
      GoogleDrive: fields.GoogleDrive ?? "",
      StrategicPriorities: fields.StrategicPriorities ?? "",
      Technologies: fields.Technologies ?? "",
      LongDescription: fields.LongDescription ?? "",
      CompanyDeepResearch: fields.CompanyDeepResearch ?? "",
      LogoUrl: fields.LogoUrl ?? "",
      "Profitable?": fields["Profitable?"] ?? "",
      interestedOpportunities: parseInterestedOpportunitiesField(fields.InterestedOpportunities),
      governmentProgramsInterest: parseGovernmentProgramsField(fields.GovernmentProgramsInterest),
      PlannedHires: fields.PlannedHires ?? "",
      LikelyCustomers: fields.LikelyCustomers ?? "",
      TopExpenses: fields.TopExpenses ?? "",
      ExpensiveVendors: fields.ExpensiveVendors ?? "",
      GovernmentGrantsPursued: fields.GovernmentGrantsPursued ?? "",
      AdditionalInfo: fields.AdditionalInfo ?? "",
      OperationalCosts: fields.OperationalCosts ?? "",
      AnnualRecurringRevenue: fields.AnnualRecurringRevenue ?? "",
      MonthlyRecurringRevenue: fields.MonthlyRecurringRevenue ?? "",
      ConversionRate: fields.ConversionRate ?? "",
      AvgDealSize: fields.AvgDealSize ?? "",
      CustomerLifetimeValue: fields.CustomerLifetimeValue ?? "",
      Debt: fields.Debt ?? "",
      AnnualTraffic: fields.AnnualTraffic ?? "",
      EmailListSize: fields.EmailListSize ?? "",
      MarketingCosts: fields.MarketingCosts ?? "",
      NewCustomersLast30Days: fields.NewCustomersLast30Days ?? "",
      QualifiedLeadsLast30Days: fields.QualifiedLeadsLast30Days ?? "",
      MeetingsBookedLast30Days: fields.MeetingsBookedLast30Days ?? "",
      ProposalsSentLast30Days: fields.ProposalsSentLast30Days ?? "",
      DealsClosedLast30Days: fields.DealsClosedLast30Days ?? "",
    };
  };

  const EDITABLE_FORM_KEYS = Object.keys(buildFormDataFromCompany());

  const snapshotFormData = (data) => JSON.stringify(data);

  const syncSavedSnapshot = (data) => {
    lastSavedSnapshotRef.current = snapshotFormData(data);
  };

  useEffect(() => {
    if (!company?.fields || !company?.id) return;
    if (prevCompanyIdRef.current === company.id && initialized.current) return;

    if (prevCompanyIdRef.current !== company.id) {
      setFormReady(false);
    }

    prevCompanyIdRef.current = company.id;
    const updatedData = buildFormDataFromCompany(company.fields);
    skipAutoSaveRef.current = true;
    syncSavedSnapshot(updatedData);
    setFormData(updatedData);
    formDataRef.current = updatedData;
    setResearchPrompt(buildCompanyDeepResearchPrompt(updatedData));
    setIsEditingResearchReport(false);
    initialized.current = true;
    setFormReady(true);
  }, [company?.id, company?.fields]);

  const getFieldValue = (fieldName) => formData[fieldName] ?? "";

  const getDirtyFormData = (data) => {
    if (!lastSavedSnapshotRef.current) {
      return Object.fromEntries(
        EDITABLE_FORM_KEYS
          .filter((key) => key in data && !isEmptyFormValue(data[key]))
          .map((key) => [key, data[key]])
      );
    }

    const saved = JSON.parse(lastSavedSnapshotRef.current);
    const dirty = {};

    EDITABLE_FORM_KEYS.forEach((key) => {
      if (!(key in data)) return;

      const next = data[key];
      const prev = saved[key];

      if (JSON.stringify(next) === JSON.stringify(prev)) return;

      if (isEmptyFormValue(next)) {
        if (CLEARABLE_FORM_KEYS.has(key) && !isEmptyFormValue(prev)) {
          dirty[key] = Array.isArray(prev) || Array.isArray(next) ? [] : "";
        }
        return;
      }

      dirty[key] = next;
    });

    return dirty;
  };

  const handleInputChange = (fieldName, value) => {
    setSaveError(null);
    setFormData((prev) => {
      const next = {
        ...prev,
        [fieldName]: value,
      };
      formDataRef.current = next;
      return next;
    });
  };

  // Mobile-friendly react-select look (matches OnboardingV2 styling, with portal-rendered menu).
  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        borderRadius: "0.5rem",
        borderColor: state.isFocused ? "#111827" : "#e5e7eb",
        boxShadow: state.isFocused ? "0 0 0 1px #111827" : "none",
        minHeight: 38,
        backgroundColor: "#ffffff",
        fontSize: "0.875rem",
        "&:hover": {
          borderColor: state.isFocused ? "#111827" : "#d1d5db",
        },
      }),
      menu: (base) => ({ ...base, fontSize: "0.875rem", zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "#f3f4f6",
        borderRadius: "0.375rem",
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: "#111827",
        fontWeight: 500,
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#6b7280",
        "&:hover": { backgroundColor: "#e5e7eb", color: "#111827" },
      }),
    }),
    []
  );

  const deepResearchSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        borderRadius: "0.5rem",
        borderColor: state.isFocused ? "#111827" : "#e5e7eb",
        boxShadow: state.isFocused ? "0 0 0 1px #111827" : "none",
        minHeight: 36,
        minWidth: 210,
        backgroundColor: "#ffffff",
        fontSize: "0.875rem",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: state.isDisabled ? 0.6 : 1,
        "&:hover": {
          borderColor: state.isFocused ? "#111827" : "#d1d5db",
        },
      }),
      valueContainer: (base) => ({ ...base, padding: "0 10px" }),
      singleValue: (base) => ({ ...base, margin: 0 }),
      menu: (base) => ({
        ...base,
        borderRadius: "0.5rem",
        border: "1px solid #f3f4f6",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
        fontSize: "0.875rem",
        zIndex: 9999,
        overflow: "hidden",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? "#f9fafb" : "#ffffff",
        color: "#111827",
        cursor: "pointer",
        padding: "8px 12px",
        "&:active": { backgroundColor: "#f3f4f6" },
      }),
      indicatorSeparator: () => ({ display: "none" }),
      dropdownIndicator: (base) => ({
        ...base,
        color: "#9ca3af",
        padding: "0 8px",
        "&:hover": { color: "#6b7280" },
      }),
    }),
    []
  );

  const selectedResearchModel =
    DEEP_RESEARCH_MODEL_OPTIONS.find((option) => option.value === researchModel) ||
    DEEP_RESEARCH_MODEL_OPTIONS[0];

  const renderCountrySelect = (colSpan = SHORT_FIELD_COL) => {
    if (loading && !formData.Country) {
      return (
        <div className={colSpan}>
          <Skeleton height={38} />
        </div>
      );
    }
    const value = formData.Country || "";
    const selected =
      countryOptions.find((o) => o.label === value || o.value === value) || null;
    return (
      <div className={colSpan}>
        <OverlappingField id="Country" label="Country">
          <Select
            inputId="Country"
            options={countryOptions}
            value={selected}
            onChange={(opt) => {
              const newCountry = opt?.label || "";
              setFormData((prev) => {
                const next = { ...prev, Country: newCountry };
                // Clear province/state if it isn't valid for the newly chosen country
                const validNames = new Set(
                  getProvinceOptionsForCountries(newCountry).map((p) => p.value)
                );
                if (prev.ProvinceOrState && !validNames.has(prev.ProvinceOrState)) {
                  next.ProvinceOrState = "";
                }
                return next;
              });
            }}
            isClearable
            placeholder="Select country..."
            styles={selectStyles}
            menuPortalTarget={REACT_SELECT_PORTAL_TARGET}
            menuPosition="fixed"
            classNamePrefix="company-info-select"
          />
        </OverlappingField>
      </div>
    );
  };

  const renderProvinceSelect = (colSpan = SHORT_FIELD_COL) => {
    if (loading && !formData.ProvinceOrState) {
      return (
        <div className={colSpan}>
          <Skeleton height={38} />
        </div>
      );
    }
    const provinceOptionsForCountry = getProvinceOptionsForCountries(formData.Country);
    const value = formData.ProvinceOrState || "";
    const selected =
      provinceOptionsForCountry.find((o) => o.value === value) || null;
    return (
      <div className={colSpan}>
        <OverlappingField id="ProvinceOrState" label="Province/State">
          <Select
            inputId="ProvinceOrState"
            options={provinceOptionsForCountry}
            value={selected}
            onChange={(opt) => handleInputChange("ProvinceOrState", opt?.value || "")}
            isClearable
            placeholder="Select province/state..."
            styles={selectStyles}
            menuPortalTarget={REACT_SELECT_PORTAL_TARGET}
            menuPosition="fixed"
            classNamePrefix="company-info-select"
          />
        </OverlappingField>
      </div>
    );
  };

  /**
   * Single-value react-select renderer for fields like CompanyType, Founded, FiscalYearEnd,
   * AnnualRevenue. `valueAsNumber` makes the option lookup numeric-tolerant (Founded and
   * AnnualRevenue are stored as numbers in Airtable but may arrive as strings from formData).
   */
  const renderReactSelect = ({
    fieldName,
    label,
    options,
    colSpan = SHORT_FIELD_COL,
    placeholder,
    valueAsNumber = false,
  }) => {
    if (loading && formData[fieldName] == null) {
      return (
        <div className={colSpan}>
          <Skeleton height={38} />
        </div>
      );
    }
    const raw = formData[fieldName];
    const selected =
      options.find((o) =>
        valueAsNumber
          ? raw != null && raw !== "" && Number(o.value) === Number(raw)
          : o.value === raw || o.label === raw
      ) || null;
    return (
      <div className={colSpan}>
        <OverlappingField id={fieldName} label={label}>
          <Select
            inputId={fieldName}
            options={options}
            value={selected}
            onChange={(opt) => handleInputChange(fieldName, opt ? opt.value : "")}
            isClearable
            placeholder={placeholder}
            styles={selectStyles}
            menuPortalTarget={REACT_SELECT_PORTAL_TARGET}
            menuPosition="fixed"
            classNamePrefix="company-info-select"
          />
        </OverlappingField>
      </div>
    );
  };

  const renderIndustryMultiSelect = () => {
    if (loading && !formData.Industry) return <Skeleton height={38} />;
    const valueArr = parseIndustryField(formData.Industry);
    const knownValues = new Set(industryOptions.map((o) => o.value));
    const legacyOptions = valueArr
      .filter((value) => value && !knownValues.has(value))
      .map((value) => ({ value, label: value }));
    const mergedIndustryOptions =
      legacyOptions.length > 0
        ? [...industryOptions, ...legacyOptions].sort((a, b) =>
            a.label.localeCompare(b.label, "en", { sensitivity: "base" })
          )
        : industryOptions;
    const selected = mergedIndustryOptions.filter((o) => valueArr.includes(o.value));
    return (
      <div className="col-span-full">
        <OverlappingField id="Industry" label="Industries">
          <Select
            inputId="Industry"
            options={mergedIndustryOptions}
            value={selected}
            isMulti
            onChange={(opts) => {
              const labels = (opts || []).map((o) => o.value);
              handleInputChange("Industry", labels.join(", "));
            }}
            placeholder="Select industries..."
            styles={selectStyles}
            menuPortalTarget={REACT_SELECT_PORTAL_TARGET}
            menuPosition="fixed"
            classNamePrefix="company-info-select"
          />
        </OverlappingField>
      </div>
    );
  };

  const persistFormData = async (data, { silent = false } = {}) => {
    if (!onSaveRef.current) return false;

    const dirtyData = getDirtyFormData(data);
    if (Object.keys(dirtyData).length === 0) {
      if (!silent) {
        setSaveStatus("idle");
        setSaveError("Nothing to save — enter a value in the field you changed.");
      }
      return false;
    }

    setSaving(true);
    setSaveStatus("saving");
    setSaveError(null);
    try {
      debugLog('CompanyProfileFields - Sending dirty formData:', dirtyData);
      const success = await onSaveRef.current(dirtyData, { silent });
      if (success !== false) {
        syncSavedSnapshot(data);
        setSaveStatus("saved");
        if (savedStatusTimeoutRef.current) clearTimeout(savedStatusTimeoutRef.current);
        savedStatusTimeoutRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        setSaveStatus("idle");
        setSaveError("Save failed. Please try again.");
      }
      return success !== false;
    } catch (error) {
      setSaveStatus("idle");
      setSaveError(error?.message || "Save failed. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    await persistFormData(formDataRef.current);
  };

  const saveButtonLabel = saving
    ? "Saving..."
    : saveStatus === "saved"
      ? "Saved"
      : "Save Changes";

  useEffect(() => {
    if (!onSaveControlsChange) return;
    onSaveControlsChange({
      handleSave,
      saving,
      saveStatus,
      saveError,
      saveButtonLabel,
    });
  }, [onSaveControlsChange, saving, saveStatus, saveError, saveButtonLabel]);

  useEffect(() => {
    if (!isEditable || !onSaveRef.current || !initialized.current || !formReady) return;

    if (skipAutoSaveRef.current) {
      skipAutoSaveRef.current = false;
      return;
    }

    const currentSnapshot = snapshotFormData(formData);
    if (currentSnapshot === lastSavedSnapshotRef.current) return;

    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      persistFormData(formDataRef.current, { silent: true });
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, [formData, isEditable, formReady]);

  useEffect(() => () => {
    if (savedStatusTimeoutRef.current) clearTimeout(savedStatusTimeoutRef.current);
  }, []);

  const handleSectionNavClick = (event, sectionId) => {
    event.preventDefault();
    setActiveSection(sectionId);
    isScrollingToSectionRef.current = true;
    scrollToCompanyProfileSection(sectionId, {
      offset: SECTION_SCROLL_OFFSET,
      onComplete: () => {
        isScrollingToSectionRef.current = false;
      },
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isScrollingToSectionRef.current) return;

      const sectionElements = sectionNavIds.map((id) => document.getElementById(id)).filter(Boolean);
      if (sectionElements.length === 0) return;

      const offset = SECTION_SCROLL_OFFSET;
      let current = sectionElements[0].id;

      for (const section of sectionElements) {
        if (section.getBoundingClientRect().top - offset <= 0) {
          current = section.id;
        }
      }

      setActiveSection((prev) => (prev === current ? prev : current));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionNavIds]);

  const resetResearchPrompt = useCallback(() => {
    const currentData = formDataRef.current;
    const existingResearch =
      currentData.CompanyDeepResearch ?? company?.fields?.CompanyDeepResearch ?? "";
    setResearchPrompt(
      buildCompanyDeepResearchPrompt({
        ...currentData,
        CompanyDeepResearch: existingResearch,
      })
    );
  }, [company?.fields?.CompanyDeepResearch]);

  const handleScrapeWebsiteContent = async () => {
    const domain = (formData.Domain ?? company?.fields?.Domain ?? "").trim();
    if (!domain) {
      setWebsiteScrapeError("Add a domain before scraping website content.");
      return;
    }

    const existingContent =
      formData.LongDescription ?? company?.fields?.LongDescription ?? "";
    if (
      existingContent.trim() &&
      !window.confirm(
        "Replace existing website content? The current text will be overwritten."
      )
    ) {
      return;
    }

    setWebsiteScrapeError(null);
    setWebsiteScrapeSuccess(false);
    setIsScrapingWebsite(true);

    try {
      const response = await fetch("/api/companyWebsiteContent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.details || result.error || "Website scrape failed.");
      }

      const updatedData = {
        ...formDataRef.current,
        LongDescription: result.content,
      };
      formDataRef.current = updatedData;
      setFormData(updatedData);
      setWebsiteScrapeSuccess(true);
      if (savedStatusTimeoutRef.current) clearTimeout(savedStatusTimeoutRef.current);
      savedStatusTimeoutRef.current = setTimeout(() => setWebsiteScrapeSuccess(false), 4000);

      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      await persistFormData(updatedData, { silent: false });
    } catch (error) {
      setWebsiteScrapeError(error.message || "Website scrape failed. Please try again.");
    } finally {
      setIsScrapingWebsite(false);
    }
  };

  const handleDeepResearch = async () => {
    if (!isAdmin) return;

    if (!formData.Name?.trim()) {
      setResearchError("Add a company name before running deep research.");
      return;
    }

    const existingResearch =
      formData.CompanyDeepResearch ?? company?.fields?.CompanyDeepResearch ?? "";
    if (
      existingResearch.trim() &&
      !window.confirm(
        "Replace existing deep research? The current report will be overwritten."
      )
    ) {
      return;
    }

    setResearchError(null);
    setResearchSuccess(false);
    setIsResearching(true);

    try {
      const response = await fetch("/api/companyDeepResearch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyInfo: {
            ...formData,
            CompanyDeepResearch: existingResearch,
          },
          researchModel,
          researchPrompt: researchPrompt.trim() || undefined,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.details || result.error || "Deep research failed.");
      }

      const updatedData = {
        ...formDataRef.current,
        CompanyDeepResearch: result.content,
      };
      formDataRef.current = updatedData;
      setFormData(updatedData);
      setIsEditingResearchReport(false);
      setResearchSuccess(true);
      if (savedStatusTimeoutRef.current) clearTimeout(savedStatusTimeoutRef.current);
      savedStatusTimeoutRef.current = setTimeout(() => setResearchSuccess(false), 4000);

      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      await persistFormData(updatedData, { silent: false });
    } catch (error) {
      setResearchError(error.message || "Deep research failed. Please try again.");
    } finally {
      setIsResearching(false);
    }
  };

  const renderWebsiteContentField = () => {
    const value = formData.LongDescription ?? company?.fields?.LongDescription ?? "";
    const domain = (formData.Domain ?? company?.fields?.Domain ?? "").trim();
    const scrapeUrl = domain ? normalizeUrl(normalizeDomain(domain)) : "";

    if (loading && !value) {
      return (
        <div className="col-span-full">
          <Skeleton height={20} />
          <div className="mt-2">
            <Skeleton height={120} />
          </div>
        </div>
      );
    }

    if (!isEditable) {
      return renderField("Website Content", value, "LongDescription", "textarea", "col-span-full");
    }

    return (
      <div className="col-span-full">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <label htmlFor="LongDescription" className="block text-sm font-medium leading-6 text-gray-900">
            Website Content
          </label>
          <button
            type="button"
            onClick={handleScrapeWebsiteContent}
            disabled={isScrapingWebsite || saving || !domain}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 sm:text-sm sm:px-4 sm:py-2"
          >
            <GlobeAltIcon className="h-4 w-4" />
            {isScrapingWebsite ? "Scraping…" : "Scrape from domain"}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {scrapeUrl
            ? `Uses Firecrawl to scrape ${scrapeUrl} into this field.`
            : "Add a domain above to enable scraping."}
        </p>
        <div className="mt-2">
          <textarea
            id="LongDescription"
            name="LongDescription"
            rows={8}
            value={getFieldValue("LongDescription")}
            onChange={(e) => handleInputChange("LongDescription", e.target.value)}
            placeholder="Website copy scraped from the company domain will appear here."
            className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors"
          />
          {value.trim() && (
            <p className="mt-1.5 text-xs text-gray-400">
              {value.trim().length.toLocaleString()} characters
            </p>
          )}
        </div>
        {websiteScrapeError && (
          <p className="mt-2 text-sm text-red-600">{websiteScrapeError}</p>
        )}
        {websiteScrapeSuccess && (
          <p className="mt-2 text-sm text-emerald-600">
            Website content saved to company profile.
          </p>
        )}
      </div>
    );
  };

  const renderDeepResearchField = () => {
    const value = formData.CompanyDeepResearch ?? company?.fields?.CompanyDeepResearch ?? "";

    if (loading && !value) {
      return (
        <div className="col-span-full">
          <Skeleton height={20} />
          <div className="mt-2">
            <Skeleton height={160} />
          </div>
        </div>
      );
    }

    if (isEditable) {
      return (
        <div className="col-span-full rounded-xl border border-gray-100 bg-gray-50/40 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <label htmlFor="CompanyDeepResearch" className="block text-sm font-semibold text-gray-900">
                Company Deep Research
              </label>
              <p className="mt-1 text-sm text-gray-600">
                AI research for grants, tax credits, and government programs. Uses your profile
                fields and web search when available. Included in PerfectDraft when populated.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:shrink-0">
              <div className="w-full sm:w-[220px]">
                <Select
                  inputId="deep-research-model"
                  options={DEEP_RESEARCH_MODEL_OPTIONS}
                  value={selectedResearchModel}
                  onChange={(option) => setResearchModel(option?.value || "gpt-5.2")}
                  isDisabled={isResearching}
                  isSearchable={false}
                  formatOptionLabel={formatDeepResearchModelOption}
                  styles={deepResearchSelectStyles}
                  menuPortalTarget={REACT_SELECT_PORTAL_TARGET}
                  menuPosition="fixed"
                  classNamePrefix="deep-research-model-select"
                  aria-label="Research model"
                />
              </div>
              <button
                type="button"
                onClick={handleDeepResearch}
                disabled={isResearching || saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <SparklesIcon className="h-4 w-4" />
                {isResearching ? "Researching…" : value.trim() ? "Refresh research" : "Run Deep Research"}
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Tip: GPT-5.2 works best for comprehensive research. Add domain and location first for
            stronger results.
          </p>
          <div className="mt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <label htmlFor="CompanyDeepResearchPrompt" className="text-sm font-medium text-gray-700">
                Research prompt
              </label>
              <button
                type="button"
                onClick={resetResearchPrompt}
                disabled={isResearching}
                className="text-xs font-medium text-gray-600 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Reset prompt
              </button>
            </div>
            <textarea
              id="CompanyDeepResearchPrompt"
              rows={8}
              value={researchPrompt}
              onChange={(e) => setResearchPrompt(e.target.value)}
              disabled={isResearching}
              placeholder="Prompt used for deep research will appear here…"
              className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-xs text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors disabled:bg-gray-50 disabled:text-gray-400"
            />
            <p className="mt-1.5 text-xs text-gray-400">
              Company profile data is included automatically. Edit instructions or section focus
              before running.
            </p>
          </div>
          {researchError && (
            <p className="mt-2 text-sm text-red-600">{researchError}</p>
          )}
          {researchSuccess && (
            <p className="mt-2 text-sm text-emerald-600">
              Research saved to company profile.
            </p>
          )}
          <div className="mt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <label htmlFor="CompanyDeepResearch" className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Research report
              </label>
              {value.trim() && (
                <button
                  type="button"
                  onClick={() => setIsEditingResearchReport((prev) => !prev)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 transition-colors hover:text-gray-900"
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" />
                  {isEditingResearchReport ? "Done editing" : "Edit report"}
                </button>
              )}
            </div>
            {isEditingResearchReport || !value.trim() ? (
              value.trim() || isEditingResearchReport ? (
                <textarea
                  id="CompanyDeepResearch"
                  name="CompanyDeepResearch"
                  rows={12}
                  value={value}
                  onChange={(e) => {
                    setResearchSuccess(false);
                    handleInputChange("CompanyDeepResearch", e.target.value);
                  }}
                  placeholder="AI-generated company research will appear here after you run deep research."
                  className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors"
                />
              ) : (
                <div id="CompanyDeepResearch" className="scroll-mt-24 rounded-lg border border-dashed border-gray-200 bg-white px-4 py-8 text-center">
                  <p className="text-sm font-medium text-gray-900">No research report yet</p>
                  <p className="mt-1 text-sm text-gray-500">
                    Run deep research to generate a report, or add one manually.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsEditingResearchReport(true)}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                    Add manually
                  </button>
                </div>
              )
            ) : (
              <div id="CompanyDeepResearch" className="max-h-[32rem] scroll-mt-24 overflow-y-auto rounded-lg border border-gray-200 bg-white px-4 py-4">
                <ReactMarkdown components={DEEP_RESEARCH_MARKDOWN_COMPONENTS}>
                  {value}
                </ReactMarkdown>
              </div>
            )}
            {value.trim() && isEditingResearchReport && (
              <p className="mt-1.5 text-xs text-gray-400">
                {value.trim().length.toLocaleString()} characters
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div id="CompanyDeepResearch" className="col-span-full scroll-mt-24">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Company Deep Research
        </label>
        <div className="mt-2">
          {value.trim() ? (
            <div className="rounded-lg border border-gray-100 bg-white px-4 py-4">
              <ReactMarkdown components={DEEP_RESEARCH_MARKDOWN_COMPONENTS}>
                {value}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-gray-500">n/a</p>
          )}
        </div>
      </div>
    );
  };

  const renderField = (label, value, fieldName = null, type = "text", colSpan = SHORT_FIELD_COL, options = null) => {
    if (loading && !value) {
      return (
        <div className={colSpan}>
          <Skeleton height={20} />
        </div>
      );
    }

    if (isEditable && fieldName) {
      const controlClass =
        type === "textarea"
          ? `${FIELD_CONTROL_CLASS} resize-y min-h-[5.5rem]`
          : FIELD_CONTROL_CLASS;
      return (
        <div className={colSpan}>
          <OverlappingField id={fieldName} label={label}>
            {type === "textarea" ? (
              <textarea
                id={fieldName}
                name={fieldName}
                rows={3}
                value={getFieldValue(fieldName)}
                onChange={(e) => handleInputChange(fieldName, e.target.value)}
                className={controlClass}
              />
            ) : type === "select" ? (
              <select
                id={fieldName}
                name={fieldName}
                value={getFieldValue(fieldName)}
                onChange={(e) => handleInputChange(fieldName, e.target.value)}
                className={controlClass}
              >
                <option value="">Select...</option>
                {options ? options.map(option => (
                  <option key={option} value={option}>{option}</option>
                )) : (
                  <>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </>
                )}
              </select>
            ) : (
              <input
                id={fieldName}
                name={fieldName}
                type={type}
                value={getFieldValue(fieldName)}
                onChange={(e) => handleInputChange(fieldName, e.target.value)}
                className={controlClass}
              />
            )}
          </OverlappingField>
        </div>
      );
    }

    return (
      <div className={colSpan} id={fieldName || undefined}>
        <OverlappingField label={label}>
          <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm text-gray-900">
            {value || "n/a"}
          </div>
        </OverlappingField>
      </div>
    );
  };

  const renderKeyAssetsOnboardingSection = () => {
    const orgChartValue = getFieldValue("OrgChart");

    const linkFields = [
      { label: "Org Chart", fieldName: "OrgChart", value: orgChartValue },
      {
        label: "Hiring Plan",
        fieldName: "HiringPlan",
        value: getFieldValue("HiringPlan"),
      },
      {
        label: "Proforma",
        fieldName: "ProformaFinancialModel",
        value: getFieldValue("ProformaFinancialModel"),
      },
      {
        label: "Google Doc",
        fieldName: "GoogleDoc",
        value: getFieldValue("GoogleDoc"),
      },
      {
        label: "Google Drive",
        fieldName: "GoogleDrive",
        value: getFieldValue("GoogleDrive"),
      },
    ];

    const strategicPrioritiesValue = getFieldValue("StrategicPriorities");

    return (
      <div id="company-key-assets" className="col-span-full scroll-mt-24 space-y-4 pt-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Key Assets</h4>
            <p className="mt-1 text-sm text-gray-500">Links used for grant discovery.</p>
          </div>
          <a
            href={SAAS_COMPANY_WORKBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
            SaaS Company Workbook
          </a>
        </div>

        <div className="grid grid-cols-1 gap-x-4 gap-y-5 pt-1 sm:grid-cols-3">
          {linkFields.map(({ label, fieldName, value }) => (
            <div key={fieldName} id={isEditable ? undefined : fieldName}>
              {!formReady ? (
                <Skeleton height={38} />
              ) : isEditable ? (
                <OverlappingField id={fieldName} label={label}>
                  <input
                    id={fieldName}
                    name={fieldName}
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    value={value}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    placeholder={`Paste ${label.toLowerCase()} URL`}
                    className={FIELD_CONTROL_CLASS}
                  />
                </OverlappingField>
              ) : (
                <OverlappingField label={label}>
                  <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm">
                    {value ? (
                      <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-gray-900 underline decoration-gray-300 underline-offset-2 transition-colors hover:decoration-gray-900"
                      >
                        {value}
                      </a>
                    ) : (
                      <span className="text-gray-400">Not linked yet</span>
                    )}
                  </div>
                </OverlappingField>
              )}
            </div>
          ))}
        </div>

        {!formReady ? (
          <Skeleton height={96} />
        ) : isEditable ? (
          <OverlappingField id="StrategicPriorities" label="Strategic Priorities">
            <textarea
              id="StrategicPriorities"
              name="StrategicPriorities"
              rows={4}
              value={strategicPrioritiesValue}
              onChange={(e) => handleInputChange("StrategicPriorities", e.target.value)}
              placeholder="What are your top strategic priorities for the next 12 months?"
              className={`${FIELD_CONTROL_CLASS} min-h-[6rem] resize-y`}
            />
          </OverlappingField>
        ) : (
          <div id="StrategicPriorities" className="scroll-mt-24">
            <OverlappingField label="Strategic Priorities">
              <p className="whitespace-pre-wrap rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm text-gray-600">
                {strategicPrioritiesValue || "Not provided yet"}
              </p>
            </OverlappingField>
          </div>
        )}
      </div>
    );
  };

  const renderLinkField = (label, value, url, fieldName = null, colSpan = SHORT_FIELD_COL, help = null) => {
    if (loading && !value) {
      return (
        <div className={colSpan}>
          <Skeleton height={20} />
        </div>
      );
    }

    if (isEditable && fieldName) {
      return (
        <div className={colSpan}>
          <OverlappingField id={fieldName} label={label} help={help ? <FieldHelpTip>{help}</FieldHelpTip> : null}>
            <input
              id={fieldName}
              name={fieldName}
              type="url"
              value={getFieldValue(fieldName)}
              onChange={(e) => handleInputChange(fieldName, e.target.value)}
              className={FIELD_CONTROL_CLASS}
            />
          </OverlappingField>
        </div>
      );
    }

    return (
      <div className={colSpan}>
        <OverlappingField label={label} help={help ? <FieldHelpTip>{help}</FieldHelpTip> : null}>
          <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm">
            {value ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900">
                {value}
              </a>
            ) : (
              <span className="text-gray-400">n/a</span>
            )}
          </div>
        </OverlappingField>
      </div>
    );
  };

  return (
    <div className={hideSectionNav ? "" : "grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-4"}>
      {!hideSectionNav && (
        <div className="self-start md:sticky md:top-0 md:z-10 md:max-h-[calc(100vh-1rem)] md:overflow-y-auto">
          <h2 className="text-base font-semibold leading-7 text-gray-900 text-left">Company Information</h2>
          <p className="mt-1 text-sm leading-6 text-gray-600">This information helps us find you the best earnings opportunities, grants, and tax credits.</p>
          <nav
            className="mt-6 flex flex-col gap-1 rounded-xl border border-gray-100 bg-white p-2"
            aria-label="Sections"
          >
            {sectionNavItems.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(event) => handleSectionNavClick(event, id)}
                className={sectionNavLinkClass(resolvedActiveSection === id)}
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      )}

      <div className={hideSectionNav ? "min-w-0 space-y-10" : "md:col-span-3 space-y-10 min-w-0"}>
        <div className="grid min-w-0 max-w-4xl grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-6">

        <SectionHeading
          id="company-interests"
          title="Interests"
          description="What we should look for first."
          action={isEditable && onSave && !hideSectionNav ? (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {saveButtonLabel}
              </button>
              {saveError && (
                <p className="text-sm text-red-600">{saveError}</p>
              )}
            </div>
          ) : null}
        />

        <div className="col-span-full grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <InterestCheckboxGroup
            id="interestedOpportunities"
            label="Earnings improvement"
            namePrefix="interested"
            options={EARNINGS_IMPROVEMENT_OPTIONS}
            selected={formData.interestedOpportunities}
            onChange={(updated) => handleInputChange("interestedOpportunities", updated)}
          />
          <InterestCheckboxGroup
            id="governmentProgramsInterest"
            label="Government programs"
            namePrefix="gov-interest"
            options={GOVERNMENT_PROGRAMS_INTEREST_OPTIONS}
            selected={formData.governmentProgramsInterest}
            onChange={(updated) => handleInputChange("governmentProgramsInterest", updated)}
          />
        </div>

        <div id="company-onboarding" className="col-span-full scroll-mt-24 pt-4">
          <OnboardingSummary companyFields={company?.fields} />
        </div>

        <SectionHeading
          id="company-general"
          title="General"
          description="Identity, size, and location."
          className="pt-4"
        />

        {renderField("Company Name", company?.fields?.Name, "Name", "text", SHORT_FIELD_COL)}
        {renderField("Domain", company?.fields?.Domain, "Domain", "text", SHORT_FIELD_COL)}
        {renderLinkField(
          "LinkedIn URL",
          company?.fields?.LinkedInUrl,
          company?.fields?.LinkedInUrl,
          "LinkedInUrl",
          SHORT_FIELD_COL,
          LINKEDIN_URL_HELP
        )}
        {renderField("Description", company?.fields?.Description, "Description", "textarea", "col-span-full")}
        {isEditable
          ? renderIndustryMultiSelect()
          : renderField("Industry", company?.fields?.Industry, "Industry", "text", "col-span-full")}
        {renderField("Employees", company?.fields?.Employees, "Employees", "number")}
        {isEditable
          ? renderReactSelect({
              fieldName: "AnnualRevenue",
              label: "Annual Revenue",
              options: moneyAmountOptionsForValue(formData.AnnualRevenue),
              placeholder: "Select annual revenue...",
              valueAsNumber: true,
            })
          : renderField(
              "Annual Revenue",
              company?.fields?.AnnualRevenue
                ? currencyFormat(company?.fields?.AnnualRevenue)
                : currencyFormat(company?.fields?.Employees * 100000),
              "AnnualRevenue",
              "number"
            )}
        {isEditable
          ? renderReactSelect({
              fieldName: "OperationalCosts",
              label: "Operational Costs ($)",
              options: moneyAmountOptionsForValue(formData.OperationalCosts),
              placeholder: "Select operational costs...",
              valueAsNumber: true,
            })
          : renderField(
              "Operational Costs ($)",
              company?.fields?.OperationalCosts ? currencyFormat(company?.fields?.OperationalCosts) : "n/a",
              "OperationalCosts",
              "number"
            )}
        {isEditable
          ? renderReactSelect({
              fieldName: "CompanyType",
              label: "Company Type",
              options: COMPANY_TYPE_OPTIONS,
              placeholder: "Select company type...",
            })
          : renderField("Company Type", company?.fields?.CompanyType, "CompanyType", "text")}
        {renderField("Profitable?", company?.fields?.['Profitable?'], "Profitable?", "select")}
        <div className={`${SHORT_FIELD_COL} flex flex-wrap items-center gap-x-5 gap-y-2 pt-2`}>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <input
              type="checkbox"
              checked={formData.IndigenousTeamMember === true}
              onChange={(event) => handleInputChange("IndigenousTeamMember", event.target.checked)}
              disabled={!isEditable}
              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            Indigenous
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <input
              type="checkbox"
              checked={formData.WomenLed === true}
              onChange={(event) => handleInputChange("WomenLed", event.target.checked)}
              disabled={!isEditable}
              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            Women-led
          </label>
        </div>
        {isEditable
          ? renderCountrySelect()
          : renderField("Country", company?.fields?.Country, "Country", "text")}
        {isEditable
          ? renderProvinceSelect()
          : renderField("Province/State", company?.fields?.ProvinceOrState, "ProvinceOrState", "text")}
        {renderField("Address", company?.fields?.Address, "Address")}
        {renderField("Legal name", company?.fields?.LegalName, "LegalName", "text")}
        {renderField("Business number", company?.fields?.BusinessNumber, "BusinessNumber", "text")}
        {isEditable
          ? renderReactSelect({
              fieldName: "Founded",
              label: "Founded",
              options: FOUNDED_YEAR_OPTIONS,
              placeholder: "Select year...",
              valueAsNumber: true,
            })
          : renderField("Founded", company?.fields?.Founded, "Founded", "number")}
        {isEditable
          ? renderReactSelect({
              fieldName: "FiscalYearEnd",
              label: "Fiscal Year End",
              options: MONTH_OPTIONS,
              placeholder: "Select month...",
            })
          : renderField("Fiscal Year End", company?.fields?.FiscalYearEnd, "FiscalYearEnd", "text")}
        {renderField("Logo URL", company?.fields?.LogoUrl, "LogoUrl", "url", SHORT_FIELD_COL)}
        {renderField("LinkedIn Follower Count", company?.fields?.FollowerCount, "FollowerCount", "number", SHORT_FIELD_COL)}
        {renderKeyAssetsOnboardingSection()}
        {renderField("Technologies", company?.fields?.Technologies, "Technologies", "textarea", "col-span-full")}
        {renderWebsiteContentField()}
        {isAdmin && renderDeepResearchField()}

        <SectionHeading
          id="company-details"
          title="Strategic Priorities & Expenses"
          description="Hiring, customers, and spend over the next year."
          className="pt-4"
        />

        {renderField("Planned Full-Time Hires", company?.fields?.PlannedHires, "PlannedHires", "select", HALF_FIELD_COL, [
          "0",
          "1-2",
          "2-5",
          "5-10",
          ">10"
        ])}
        {renderField("Likely Customers", company?.fields?.LikelyCustomers, "LikelyCustomers", "text", HALF_FIELD_COL)}
        {renderField("Top Expenses Next 12 Months", company?.fields?.TopExpenses, "TopExpenses", "textarea", "col-span-full")}
        {renderField("Most Expensive Vendors", company?.fields?.ExpensiveVendors, "ExpensiveVendors", "textarea", "col-span-full")}
        {renderField("Government Grants Pursued", company?.fields?.GovernmentGrantsPursued, "GovernmentGrantsPursued", "textarea", "col-span-full")}
        {renderField("Additional Information", company?.fields?.AdditionalInfo, "AdditionalInfo", "textarea", "col-span-full")}

        {isAdmin && (
          <>
            <SectionHeading id="company-team" title="Team" className="pt-4" />
            <CompanyTeamSection company={company} />
          </>
        )}

        </div>

        <div className="grid min-w-0 max-w-4xl grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-6">

        <SectionHeading
          id="company-metrics"
          title="Metrics"
          description="Pipeline and revenue numbers."
          className="pt-4"
        />

        {renderField("Annual Recurring Revenue (ARR)", company?.fields?.AnnualRecurringRevenue ? currencyFormat(company?.fields?.AnnualRecurringRevenue) : "n/a", "AnnualRecurringRevenue", "number", HALF_FIELD_COL)}
        {renderField("Monthly Recurring Revenue (MRR)", company?.fields?.MonthlyRecurringRevenue ? currencyFormat(company?.fields?.MonthlyRecurringRevenue) : "n/a", "MonthlyRecurringRevenue", "number", HALF_FIELD_COL)}
        {renderField("Conversion Rate (%)", company?.fields?.ConversionRate ? company?.fields?.ConversionRate + "%" : "2%", "ConversionRate", "number", HALF_FIELD_COL)}
        {renderField("Average Deal Size ($)", currencyFormat(company?.fields?.AvgDealSize ? company?.fields?.AvgDealSize : 12250), "AvgDealSize", "number", HALF_FIELD_COL)}
        {renderField("Customer Lifetime Value (LTV)", company?.fields?.CustomerLifetimeValue, "CustomerLifetimeValue", "number", HALF_FIELD_COL)}
        {renderField("Debt ($)", company?.fields?.Debt, "Debt", "number", HALF_FIELD_COL)}
        {renderField("Annual Traffic", company?.fields?.AnnualTraffic, "AnnualTraffic", "number", HALF_FIELD_COL)}
        {renderField("Email List Size", company?.fields?.EmailListSize, "EmailListSize", "number", HALF_FIELD_COL)}
        {renderField("Marketing Costs (Last 30 Days)", company?.fields?.MarketingCosts, "MarketingCosts", "number", HALF_FIELD_COL)}
        {renderField("New Customers (Last 30 Days)", company?.fields?.NewCustomersLast30Days, "NewCustomersLast30Days", "number", HALF_FIELD_COL)}
        {renderField("Qualified Leads (Last 30 Days)", company?.fields?.QualifiedLeadsLast30Days, "QualifiedLeadsLast30Days", "number", HALF_FIELD_COL)}
        {renderField("Meetings Booked (Last 30 Days)", company?.fields?.MeetingsBookedLast30Days, "MeetingsBookedLast30Days", "number", HALF_FIELD_COL)}
        {renderField("Proposals Sent (Last 30 Days)", company?.fields?.ProposalsSentLast30Days, "ProposalsSentLast30Days", "number", HALF_FIELD_COL)}
        {renderField("Deals Closed (Last 30 Days)", company?.fields?.DealsClosedLast30Days, "DealsClosedLast30Days", "number", HALF_FIELD_COL)}

        </div>

        <div id="company-grant-qa" className="min-w-0 scroll-mt-24 pt-2">
          <CompanyGrantQuestionsSection company={company} />
        </div>

        {isAdmin && (
          <div id="company-call-recordings" className="min-w-0 scroll-mt-24 pt-8">
            <CompanyCallRecordingsSection company={company} />
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyProfileFields; 